// v6 F11.3 — Extra posten / opties / kortingen sectie
//
// Vervangt de oude "Algemene extra posten"-kaart. Projectbreed
// (extra_lines zonder room_id), ondersteunt drie types via `kind`:
//   • extra    → gewone bijkomende post, in subtotaal + BTW
//   • option   → optionele post, apart, NIET in hoofdtotaal
//   • discount → aftrek (vast €-bedrag óf percentage, exclusief)
//
// Vier acties bovenaan:
//   1. Sjabloon toepassen (Emma™-Meerwerken standaard / regie)
//   2. Post uit prijslijst toevoegen (zoekbare picker)
//   3. Vrije post toevoegen (custom)
//   4. Korting toevoegen (€ of %)
//
// Standaard ingeklapt wanneer leeg. Compacte badges per rij.
import React, { useMemo, useState } from "react";
import { Package, Plus, Trash2, ChevronDown, ChevronUp, Search } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import RawNumInput from "./RawNumInput";

const newId = () => `x_${Math.random().toString(36).slice(2, 11)}`;

const KIND_LABELS = {
  extra:    { label: "Extra",   cls: "bg-[#F0EFEA] text-[#5D7259] border-[#5D7259]/30" },
  option:   { label: "Optie",   cls: "bg-[#FFF3E4] text-[#A04A2D] border-[#D96C4A]/40" },
  discount: { label: "Korting", cls: "bg-[#FCE9E6] text-[#A03127] border-[#D9534F]/40" },
};

export default function ExtraPostenCard({
  extras,          // extra_lines (project-scope: room_id === null)
  templates = [],
  priceCodes = [],
  onAdd,           // (partialExtraLine) → void
  onUpdate,        // (id, patch) → void
  onRemove,        // (id) → void
}) {
  const [open, setOpen] = useState(extras.length > 0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [tplPickerOpen, setTplPickerOpen] = useState(false);

  // Emma™-templates die geschikt zijn voor extras (category general OF meerwerken).
  const applicableTemplates = useMemo(() => templates.filter((t) => {
    if (t.active === false) return false;
    const cats = t.categories?.length ? t.categories : [t.category];
    return cats.includes("general");
  }), [templates]);

  const applyTemplate = (tpl) => {
    const pcById = Object.fromEntries(priceCodes.map((p) => [p.id, p]));
    (tpl.template_lines || []).forEach((ln) => {
      if (ln.default_checked === false) return;
      const pc = pcById[ln.price_code_id];
      const description = ln.description_override || pc?.description || tpl.name;
      const unit_price = ln.unit_price_override != null ? +ln.unit_price_override : (pc?.default_price ?? 0);
      onAdd({
        id: newId(),
        room_id: null,
        kind: "extra",
        description,
        quantity: ln.suggested_qty ?? 1,
        unit: pc?.unit || "fixed",
        unit_price,
        vat_rate: 21,
        source_price_code_id: ln.price_code_id,
      });
    });
    setTplPickerOpen(false);
  };

  const addFromPriceCode = (pc) => {
    onAdd({
      id: newId(),
      room_id: null,
      kind: "extra",
      description: pc.description,
      quantity: 1,
      unit: pc.unit || "fixed",
      unit_price: pc.default_price || 0,
      vat_rate: 21,
      source_price_code_id: pc.id,
    });
    setPickerOpen(false);
  };

  const addFreePost = () => onAdd({
    id: newId(), room_id: null, kind: "extra",
    description: "", quantity: 1, unit: "fixed", unit_price: 0, vat_rate: 21,
  });

  const addOption = () => onAdd({
    id: newId(), room_id: null, kind: "option",
    description: "", quantity: 1, unit: "fixed", unit_price: 0, vat_rate: 21,
  });

  const addDiscount = () => onAdd({
    id: newId(), room_id: null, kind: "discount",
    description: "", quantity: 1, unit: "fixed",
    unit_price: 0, discount_pct: null, vat_rate: 21,
  });

  const isEmpty = extras.length === 0;
  // v6 F12b — Uniform inklap-gedrag: ingeklapt bij leeg, uitgeklapt bij posten.
  // User kan altijd manueel wisselen via chevron.
  const forceOpen = open;

  return (
    <div className="emma-card" data-testid="project-extras-card">
      {/* v6 F12b — Uniforme collapsible header met chevron LINKS */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 mb-1 text-left"
        data-testid="extras-toggle"
      >
        <ChevronDown className={`w-4 h-4 text-[#5D7259] transition-transform ${forceOpen ? "" : "-rotate-90"}`} />
        <Package className="w-4 h-4 text-[#5D7259]" />
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-semibold">Extra posten / opties</h3>
          <div className="text-[11px] text-[#4F4F4D]">
            Meerwerken, regie, werfkosten, opties en kortingen voor dit project.
          </div>
        </div>
        {extras.length > 0 && (
          <span className="text-xs px-2 h-6 rounded-full bg-[#F0EFEA] text-[#5D7259] font-medium inline-flex items-center gap-1">
            {extras.length} post{extras.length === 1 ? "" : "en"}
          </span>
        )}
      </button>

      {forceOpen && (
        <>
          {/* Actie-knoppen */}
          <div className="flex flex-wrap gap-2 my-3">
            {applicableTemplates.length > 0 && (
              <Button variant="outline" onClick={() => setTplPickerOpen((v) => !v)}
                className="h-10 rounded-xl text-xs gap-1" data-testid="extras-add-template">
                <Plus className="w-3 h-3" /> Sjabloon toepassen
              </Button>
            )}
            <Button variant="outline" onClick={() => setPickerOpen((v) => !v)}
              className="h-10 rounded-xl text-xs gap-1" data-testid="extras-add-from-pricelist">
              <Plus className="w-3 h-3" /> Post uit prijslijst
            </Button>
            <Button variant="outline" onClick={addFreePost}
              className="h-10 rounded-xl text-xs gap-1 border-dashed" data-testid="extras-add-free">
              <Plus className="w-3 h-3" /> Vrije post
            </Button>
            <Button variant="outline" onClick={addOption}
              className="h-10 rounded-xl text-xs gap-1" data-testid="extras-add-option">
              <Plus className="w-3 h-3" /> Optie
            </Button>
            <Button variant="outline" onClick={addDiscount}
              className="h-10 rounded-xl text-xs gap-1 text-[#A03127] border-[#D9534F]/40 hover:bg-[#FCE9E6]" data-testid="extras-add-discount">
              <Plus className="w-3 h-3" /> Korting
            </Button>
          </div>

          {/* Template picker */}
          {tplPickerOpen && (
            <div className="border border-[#E5E2DC] rounded-xl p-3 mb-3 bg-[#FAF9F6]" data-testid="extras-template-picker">
              <div className="text-[11px] uppercase tracking-wider font-bold text-[#5D7259] mb-2">
                Kies een sjabloon om toe te passen
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {applicableTemplates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => applyTemplate(t)}
                    className="text-left px-3 py-2 bg-white border border-[#E5E2DC] rounded-lg hover:border-[#D96C4A] transition"
                    data-testid={`extras-tpl-${t.id}`}
                  >
                    <div className="font-medium text-sm">{t.name}</div>
                    <div className="text-[11px] text-[#5D7259]">
                      {(t.template_lines || []).filter((ln) => ln.default_checked !== false).length} standaardregels
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* PriceCode picker */}
          {pickerOpen && (
            <PriceCodePicker
              priceCodes={priceCodes}
              onPick={addFromPriceCode}
              onClose={() => setPickerOpen(false)}
            />
          )}

          {/* Empty state */}
          {isEmpty && (
            <div className="text-sm text-[#4F4F4D] py-3 px-3 border border-dashed border-[#E5E2DC] rounded-xl italic">
              Nog geen extra posten toegevoegd. Kies hierboven een actie om te starten.
            </div>
          )}

          {/* Rows */}
          {extras.map((e) => (
            <ExtraLineRow key={e.id} e={e}
              onChange={(patch) => onUpdate(e.id, patch)}
              onRemove={() => onRemove(e.id)} />
          ))}

          {/* v6 F11.3 — Live samenvatting-strip (compact, niet sticky).
              Toont enkel per-type totalen van de eigen kaart-content. Voor
              percentage-kortingen tonen we het percentage, want het €-bedrag
              is pas bekend na volledige offerteberekening. */}
          <ExtrasSummaryStrip extras={extras} />
        </>
      )}
    </div>
  );
}

function ExtrasSummaryStrip({ extras }) {
  const fmtEUR = (n) => {
    try {
      return new Intl.NumberFormat("nl-BE", {
        style: "currency", currency: "EUR",
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      }).format(+n || 0).replace(/\u00A0/g, " ");
    } catch { return `€ ${(+n || 0).toFixed(2).replace(".", ",")}`; }
  };

  const extrasSum = extras
    .filter((e) => e.kind === "extra")
    .reduce((s, e) => s + (+e.quantity || 0) * (+e.unit_price || 0), 0);

  const optionRows = extras.filter((e) => e.kind === "option");
  const optExcl = optionRows.reduce((s, e) => s + (+e.quantity || 0) * (+e.unit_price || 0), 0);
  const optIncl = optionRows.reduce((s, e) => {
    const excl = (+e.quantity || 0) * (+e.unit_price || 0);
    return s + excl * (1 + (+e.vat_rate || 0) / 100);
  }, 0);

  const discountRows = extras.filter((e) => e.kind === "discount");
  const discFixed = discountRows
    .filter((e) => !(e.discount_pct > 0))
    .reduce((s, e) => s + (+e.unit_price || 0), 0);
  const discPctTotal = discountRows
    .filter((e) => +e.discount_pct > 0)
    .reduce((s, e) => s + (+e.discount_pct || 0), 0);

  const hasExtras = extrasSum > 0;
  const hasOptions = optionRows.length > 0 && optExcl > 0;
  const hasDiscount = discFixed > 0 || discPctTotal > 0;

  if (!hasExtras && !hasOptions && !hasDiscount) return null;

  return (
    <div
      className="mt-3 pt-3 border-t border-[#E5E2DC] flex flex-wrap items-center gap-x-4 gap-y-1 text-xs"
      data-testid="extras-summary-strip"
    >
      {hasExtras && (
        <div className="inline-flex items-center gap-1.5" data-testid="strip-extras">
          <span className="text-[#5D7259] font-medium">Extra&apos;s</span>
          <strong className="text-[#2C2C2A] tabular-nums">{fmtEUR(extrasSum)}</strong>
          <span className="text-[10px] text-[#4F4F4D]">excl. BTW</span>
        </div>
      )}
      {hasOptions && (
        <div className="inline-flex items-center gap-1.5" data-testid="strip-options">
          <span className="text-[#A04A2D] font-medium">Opties</span>
          <strong className="text-[#2C2C2A] tabular-nums">{fmtEUR(optExcl)}</strong>
          <span className="text-[10px] text-[#4F4F4D]">excl.</span>
          <span className="text-[10px] text-[#4F4F4D]">·</span>
          <strong className="text-[#2C2C2A] tabular-nums">{fmtEUR(optIncl)}</strong>
          <span className="text-[10px] text-[#4F4F4D]">incl.</span>
          <span className="text-[10px] italic text-[#A04A2D]/70">(niet inbegrepen)</span>
        </div>
      )}
      {hasDiscount && (
        <div className="inline-flex items-center gap-1.5" data-testid="strip-discount">
          <span className="text-[#A03127] font-medium">Korting</span>
          {discFixed > 0 && (
            <strong className="text-[#A03127] tabular-nums" data-testid="strip-discount-eur">
              −{fmtEUR(discFixed)}
            </strong>
          )}
          {discPctTotal > 0 && (
            <strong className="text-[#A03127] tabular-nums" data-testid="strip-discount-pct">
              −{discPctTotal.toFixed(2).replace(".", ",")}%
            </strong>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Eén extra-rij (aanpasbaar aan kind)
// ============================================================================
function ExtraLineRow({ e, onChange, onRemove }) {
  const badge = KIND_LABELS[e.kind || "extra"];
  const isDiscount = e.kind === "discount";
  const isPct = isDiscount && e.discount_pct != null && +e.discount_pct > 0;

  return (
    <div className="mt-2 border border-[#E5E2DC] rounded-xl p-2.5 bg-white space-y-2"
      data-testid={`extra-row-${e.id}`}>
      <div className="flex items-center gap-2">
        <span className={`text-[10px] px-2 h-6 rounded-full border font-bold inline-flex items-center ${badge.cls}`}
          data-testid={`extra-badge-${e.id}`}>
          {badge.label}
        </span>
        <Input
          value={e.description || ""}
          onChange={(ev) => onChange({ description: ev.target.value })}
          className="h-9 rounded-lg text-sm flex-1"
          placeholder={isDiscount ? "Bv. Trouwe-klant korting" : "Omschrijving"}
          data-testid={`extra-desc-${e.id}`}
        />
        <button onClick={onRemove}
          className="w-9 h-9 rounded-lg hover:bg-red-50 text-[#D9534F] flex items-center justify-center"
          data-testid={`extra-remove-${e.id}`} aria-label="Verwijder">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {isDiscount ? (
        <DiscountFields e={e} onChange={onChange} isPct={isPct} />
      ) : (
        <ExtraFields e={e} onChange={onChange} />
      )}
    </div>
  );
}

function ExtraFields({ e, onChange }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
      <div>
        <label className="text-[10px] uppercase text-[#5D7259] font-bold">Aantal</label>
        <RawNumInput value={e.quantity} onChange={(v) => onChange({ quantity: Math.max(0, +v || 0) })}
          className="h-9 rounded-lg mt-0.5" data-testid={`extra-qty-${e.id}`} />
      </div>
      <div>
        <label className="text-[10px] uppercase text-[#5D7259] font-bold">Eenheid</label>
        <select value={e.unit || "fixed"} onChange={(ev) => onChange({ unit: ev.target.value })}
          className="h-9 w-full rounded-lg border border-[#E5E2DC] bg-white px-2 mt-0.5"
          data-testid={`extra-unit-${e.id}`}>
          <option value="fixed">forfait</option>
          <option value="m2">m²</option>
          <option value="piece">stuk</option>
          <option value="lm">lm</option>
          <option value="hour">uur</option>
        </select>
      </div>
      <div>
        <label className="text-[10px] uppercase text-[#5D7259] font-bold">€ / eenheid</label>
        <RawNumInput value={e.unit_price} onChange={(v) => onChange({ unit_price: Math.max(0, +v || 0) })}
          className="h-9 rounded-lg mt-0.5" data-testid={`extra-price-${e.id}`} />
      </div>
      <div>
        <label className="text-[10px] uppercase text-[#5D7259] font-bold">BTW %</label>
        <select value={e.vat_rate ?? 21} onChange={(ev) => onChange({ vat_rate: +ev.target.value })}
          className="h-9 w-full rounded-lg border border-[#E5E2DC] bg-white px-2 mt-0.5"
          data-testid={`extra-vat-${e.id}`}>
          <option value={0}>0%</option>
          <option value={6}>6%</option>
          <option value={21}>21%</option>
        </select>
      </div>
      <div className="col-span-2 sm:col-span-1 text-right">
        <label className="text-[10px] uppercase text-[#5D7259] font-bold">Totaal excl.</label>
        <div className="h-9 flex items-center justify-end font-bold text-sm text-[#2C2C2A]"
          data-testid={`extra-total-${e.id}`}>
          € {(((+e.quantity) || 0) * ((+e.unit_price) || 0)).toFixed(2)}
        </div>
      </div>
    </div>
  );
}

function DiscountFields({ e, onChange, isPct }) {
  const setMode = (mode) => {
    if (mode === "pct") onChange({ unit_price: 0, discount_pct: e.discount_pct ?? 5 });
    else onChange({ discount_pct: null, unit_price: e.unit_price || 0 });
  };
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs items-end">
      <div>
        <label className="text-[10px] uppercase text-[#5D7259] font-bold">Type korting</label>
        <div className="flex gap-1 mt-0.5">
          <button type="button" onClick={() => setMode("eur")}
            className={`flex-1 h-9 rounded-lg text-xs font-bold border ${!isPct ? "bg-[#A03127] text-white border-[#A03127]" : "bg-white border-[#E5E2DC]"}`}
            data-testid={`extra-disc-mode-eur-${e.id}`}>
            € Vast
          </button>
          <button type="button" onClick={() => setMode("pct")}
            className={`flex-1 h-9 rounded-lg text-xs font-bold border ${isPct ? "bg-[#A03127] text-white border-[#A03127]" : "bg-white border-[#E5E2DC]"}`}
            data-testid={`extra-disc-mode-pct-${e.id}`}>
            % van subt.
          </button>
        </div>
      </div>
      {isPct ? (
        <div>
          <label className="text-[10px] uppercase text-[#5D7259] font-bold">Percentage</label>
          <div className="flex items-center gap-1 mt-0.5">
            <RawNumInput value={e.discount_pct ?? 0}
              onChange={(v) => onChange({ discount_pct: Math.min(100, Math.max(0, +v || 0)) })}
              className="h-9 rounded-lg" data-testid={`extra-disc-pct-${e.id}`} />
            <span className="text-[#5D7259] font-bold">%</span>
          </div>
        </div>
      ) : (
        <div>
          <label className="text-[10px] uppercase text-[#5D7259] font-bold">Bedrag €</label>
          <RawNumInput value={e.unit_price ?? 0}
            onChange={(v) => onChange({ unit_price: Math.max(0, +v || 0) })}
            className="h-9 rounded-lg mt-0.5" data-testid={`extra-disc-eur-${e.id}`} />
        </div>
      )}
      <div className="text-right">
        <div className="text-[10px] uppercase text-[#5D7259] font-bold">Info</div>
        <div className="text-[11px] text-[#4F4F4D] leading-tight mt-0.5">
          Pro rata verdeeld over BTW-tarieven; nooit onder €0 belastbaar.
        </div>
      </div>
    </div>
  );
}

// Simple pricelist picker with search
function PriceCodePicker({ priceCodes, onPick, onClose }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    return priceCodes
      .filter((p) => p.active !== false)
      .filter((p) => !t || (p.code || "").toLowerCase().includes(t) || (p.description || "").toLowerCase().includes(t))
      .slice(0, 40);
  }, [q, priceCodes]);
  return (
    <div className="border border-[#E5E2DC] rounded-xl p-3 mb-3 bg-[#FAF9F6]" data-testid="extras-pricelist-picker">
      <div className="flex items-center gap-2 mb-2">
        <Search className="w-4 h-4 text-[#5D7259]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Zoek code of omschrijving…"
          className="h-9 rounded-lg flex-1" data-testid="extras-pricelist-search" autoFocus />
        <button onClick={onClose} className="h-9 px-3 text-xs rounded-lg border border-[#E5E2DC] hover:bg-white"
          data-testid="extras-pricelist-close">Sluit</button>
      </div>
      <div className="max-h-64 overflow-y-auto divide-y divide-[#E5E2DC] bg-white rounded-lg border border-[#E5E2DC]">
        {filtered.length === 0 && <div className="p-3 text-xs text-[#4F4F4D]">Geen resultaten.</div>}
        {filtered.map((p) => (
          <button key={p.id} onClick={() => onPick(p)}
            className="w-full text-left px-3 py-2 hover:bg-[#FFF3E4] transition flex items-center justify-between gap-3"
            data-testid={`extras-pc-${p.id}`}>
            <div className="min-w-0">
              <div className="font-mono text-xs text-[#5D7259]">{p.code}</div>
              <div className="text-sm truncate">{p.description}</div>
            </div>
            <div className="text-xs whitespace-nowrap">
              € {(+p.default_price || 0).toFixed(2)} / {p.unit}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
